// Step4Invitations — ActivityWizard Steg 4 (delspår 3e Fas 2c). Replaces the 3g placeholder.
// Invite registered org members (SearchablePlayerSelector, requireRegistered) + external emails
// (parseEmailList). Selections sync into formData.invitedUserIds / invitedEmails (UPDATE_FORM).
// Gate: always optional (footer Nästa stays active even with 0 invitees — locked decision #5).
// The actual invitation rows + email are created post-create (ActivityWizard.submitActivity).

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { UserPlus, Mail, X } from "lucide-react";
import { useActivityWizard } from "@/components/activity/wizard/ActivityWizardContext";
import { useOrganization } from "@/hooks/useOrganization";
import SearchablePlayerSelector, {
  type SearchablePlayer,
} from "@/components/admin/SearchablePlayerSelector";
import { parseEmailList } from "@/lib/teamInviteHelpers";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import WizardStepHero from "@/components/activity/wizard/WizardStepHero";

export default function Step4Invitations() {
  const { t } = useTranslation("admin");
  const { state, dispatch } = useActivityWizard();
  const { organization } = useOrganization();
  const fd = state.formData;
  const isPrivate = fd.visibility === "private";

  // Local display state (names for chips); formData holds the id/email arrays the RPC needs.
  const [players, setPlayers] = useState<SearchablePlayer[]>([]);
  const [emails, setEmails] = useState<string[]>(fd.invitedEmails ?? []);
  const [emailInput, setEmailInput] = useState("");
  const [emailError, setEmailError] = useState(false);

  const syncPlayers = (next: SearchablePlayer[]) => {
    setPlayers(next);
    dispatch({
      type: "UPDATE_FORM",
      payload: { invitedUserIds: next.map((p) => p.user_id).filter(Boolean) as string[] },
    });
  };
  const syncEmails = (next: string[]) => {
    setEmails(next);
    dispatch({ type: "UPDATE_FORM", payload: { invitedEmails: next } });
  };

  const addPlayer = (p: SearchablePlayer) => {
    if (!p.user_id) return;
    if (players.some((x) => x.user_id === p.user_id)) return;
    syncPlayers([...players, p]);
  };
  const removePlayer = (userId: string) => syncPlayers(players.filter((p) => p.user_id !== userId));

  const addEmails = () => {
    const parsed = parseEmailList(emailInput);
    if (parsed.length === 0) {
      setEmailError(emailInput.trim().length > 0);
      return;
    }
    const merged = Array.from(new Set([...emails, ...parsed]));
    syncEmails(merged);
    setEmailInput("");
    setEmailError(false);
  };
  const removeEmail = (email: string) => syncEmails(emails.filter((e) => e !== email));

  const excludeUserIds = players.map((p) => p.user_id).filter(Boolean) as string[];

  return (
    <section className="space-y-5">
      <WizardStepHero
        title={t("activityWizard.step4.heroTitle", { defaultValue: "Bjud in spelare" })}
        subtitle={t("activityWizard.step4.heroSubtitle", {
          defaultValue: "Lägg till medlemmar eller dela en länk — valfritt.",
        })}
      />
      {/* Hero already carries the "Bjud in spelare" title — keep only the public/private explain. */}
      <p className="text-sm text-muted-foreground">
        {isPrivate
          ? t("activityWizard.step4.explainPrivate", { defaultValue: "Aktiviteten är privat — bara inbjudna ser den och kan gå med." })
          : t("activityWizard.step4.explainPublic", { defaultValue: "Aktiviteten är öppen för alla i klubben. Du kan bjuda in extra spelare." })}
      </p>

      {/* Registered members */}
      <div className="space-y-2" data-testid="invite-player-search">
        <label className="flex items-center gap-2 text-sm font-medium">
          <UserPlus className="h-4 w-4" />
          {t("activityWizard.step4.invitePlayers", { defaultValue: "Bjud in medlemmar" })}
        </label>
        {organization?.id && (
          <SearchablePlayerSelector
            organizationId={organization.id}
            requireRegistered
            excludeUserIds={excludeUserIds}
            onSelect={addPlayer}
            placeholder={t("activityWizard.step4.searchPlaceholder", { defaultValue: "Sök spelare…" })}
          />
        )}
      </div>

      {/* External emails */}
      <div className="space-y-2">
        <label className="flex items-center gap-2 text-sm font-medium">
          <Mail className="h-4 w-4" />
          {t("activityWizard.step4.inviteByEmail", { defaultValue: "Bjud in via e-post" })}
        </label>
        <div className="flex gap-2">
          <Input
            type="email"
            data-testid="invite-email-input"
            value={emailInput}
            onChange={(e) => { setEmailInput(e.target.value); setEmailError(false); }}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addEmails(); } }}
            placeholder={t("activityWizard.step4.emailPlaceholder", { defaultValue: "namn@exempel.se" })}
            className="rounded-xl"
          />
          <Button type="button" variant="outline" data-testid="invite-email-add" onClick={addEmails} className="rounded-xl">
            {t("activityWizard.step4.addEmail", { defaultValue: "Lägg till" })}
          </Button>
        </div>
        {emailError && (
          <p className="text-sm text-destructive" data-testid="invite-email-error">
            {t("activityWizard.step4.errors.invalidEmail", { defaultValue: "Ogiltig e-postadress" })}
          </p>
        )}
      </div>

      {/* Invitee list */}
      {(players.length > 0 || emails.length > 0) && (
        <div className="space-y-2" data-testid="invitee-list">
          <p className="text-sm font-medium">
            {t("activityWizard.step4.inviteeListHeading", { defaultValue: "Inbjudna" })} ({players.length + emails.length})
          </p>
          <ul className="flex flex-wrap gap-2">
            {players.map((p) => (
              <li key={p.user_id ?? p.customer_id} data-testid={`invitee-chip-${p.user_id}`}
                  className="livfull-pill-selected flex items-center gap-1 rounded-full border px-3 py-1 text-sm">
                {p.full_name}
                <button type="button" aria-label={t("activityWizard.step4.removeInvitee", { defaultValue: "Ta bort" })}
                        data-testid="invitee-remove" onClick={() => removePlayer(p.user_id!)}>
                  <X className="h-3.5 w-3.5 text-primary/60 hover:text-primary" />
                </button>
              </li>
            ))}
            {emails.map((e) => (
              <li key={e} data-testid={`invitee-chip-${e}`}
                  className="livfull-pill-selected flex items-center gap-1 rounded-full border px-3 py-1 text-sm">
                {e}
                <button type="button" aria-label={t("activityWizard.step4.removeInvitee", { defaultValue: "Ta bort" })}
                        data-testid="invitee-remove" onClick={() => removeEmail(e)}>
                  <X className="h-3.5 w-3.5 text-primary/60 hover:text-primary" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
